import React from 'react';
import { EmployeeSection } from '../../components/EmployeeSection';
import { DatePicker, Field, Input, Select, Show } from '@welbe/design-system';
import { useEmployee } from '../../Employee.hooks';
import { getBranchOffices } from 'services/filters';
import { useEffect, useState } from 'react';
import { useSessionStorage } from 'hooks/useSessionStorage';
import type { OptionType } from '../../Employee.types';
import { Controller } from 'react-hook-form';

export const Company = ({
	errors,
	disabled,
}: {
	errors: string[];
	disabled?: boolean;
}) => {
	const [companyLoading, setCompanyLoading] = useState(false);
	const { getSessionStorage } = useSessionStorage();
	const { methods } = useEmployee();
	const { control, register, setValue } = methods;
	const [loadingBranchOffices, setLoadingBranchOffices] = useState(false);
	const [branchOfficeOptions, setBranchOfficeOptions] = useState<OptionType[]>(
		[],
	);

	const fetchCurrentCompany = () => {
		setCompanyLoading(true);
		setTimeout(() => {
			const currentUser = JSON.parse(getSessionStorage('me') || '{}');
			setValue('companyId', currentUser.company.id);
			setValue('companyFancyName', currentUser.company.fancyName);
			setCompanyLoading(false);
		}, 2000);
	};

	const fetchBranchOfficeOptions = async (): Promise<void> => {
		try {
			const res = await getBranchOffices();
			const { branchOffices } = res.data.content;
			setBranchOfficeOptions(branchOffices);
		} catch (error) {
			console.error('Error fetching branch offices:', error);
		} finally {
			setLoadingBranchOffices(false);
		}
	};

	useEffect(() => {
		fetchCurrentCompany();
		fetchBranchOfficeOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<EmployeeSection
			id="company"
			title="Empresa"
			description="Completa la información con los datos de la empresa a la cual se asociará el colaborador"
		>
			<Field.Root required invalid={errors.includes('companyFancyName')}>
				<Field.Label>
					Empresa <Field.RequiredIndicator />
				</Field.Label>
				<Show
					when={!companyLoading}
					fallback={<Input placeholder="Cargando..." readOnly />}
				>
					<Input
						placeholder="Selecciona una Empresa"
						readOnly
						{...register('companyFancyName')}
					/>
				</Show>
			</Field.Root>
			<Controller
				name="branchOfficeId"
				control={control}
				render={({ field }) => (
					<Select.Snippet
						required
						invalid={errors.includes('branchOfficeId') && !field.value}
						options={branchOfficeOptions}
						loading={loadingBranchOffices}
						disabled={loadingBranchOffices}
						readOnly={disabled}
						label="Sucursal"
						placeholder="Seleccione una opción"
						value={field.value ? [field.value] : []}
						onValueChange={(selected: any) => {
							field.onChange(selected.id);
							setValue('branchOfficeName', selected.name);
						}}
					/>
				)}
			/>
			<Field.Root required invalid={errors.includes('companyBadgeNumber')}>
				<Field.Label>
					Número de empleado <Field.RequiredIndicator />
				</Field.Label>
				<Input
					placeholder="Escribe el número de empleado"
					readOnly={disabled}
					{...register('companyBadgeNumber')}
				/>
			</Field.Root>
			<Field.Root required invalid={errors.includes('corporateEmail')}>
				<Field.Label>
					Correo corporativo <Field.RequiredIndicator />
				</Field.Label>
				<Input
					type="email"
					placeholder="nombre@dominio"
					readOnly={disabled}
					{...register('corporateEmail')}
				/>
			</Field.Root>
			<Controller
				name="hiringDate"
				control={control}
				render={({ field }) => (
					<DatePicker.Snippet
						label="Fecha de ingreso"
						placeholder="Selecciona una fecha"
						readOnly={disabled}
						value={field.value ? field.value : undefined}
						onChange={(date) =>
							field.onChange(date?.split('/').reverse().join('-'))
						}
					/>
				)}
			/>
			<Field.Root>
				<Field.Label>Sector</Field.Label>
				<Input
					placeholder="Escribe el sector"
					{...register('sector')}
					readOnly={disabled}
				/>
			</Field.Root>
			<Field.Root>
				<Field.Label>Turno</Field.Label>
				<Input
					placeholder="Escribe el turno"
					{...register('shift')}
					readOnly={disabled}
				/>
			</Field.Root>
			<Field.Root>
				<Field.Label>Puesto</Field.Label>
				<Input
					placeholder="Escribe el puesto"
					{...register('position')}
					readOnly={disabled}
				/>
			</Field.Root>
		</EmployeeSection>
	);
};
